// Wires "click a sticky during vote phase" → cast/remove a vote.
//
// Behaviour:
//   - Active only when meta.phase === 'vote'
//   - Watches selectedIds: when the user selects exactly one vote-eligible
//     shape, toggle their vote on it (cast if they haven't voted on it yet
//     and have budget remaining; otherwise remove their existing vote).
//   - Clears the selection after each vote so consecutive clicks cast
//     consecutive votes without needing to deselect manually.
//
// vote-eligible = sticky | rect | ellipse | text (any text-bearing primitive
// participants typically use). Arrows / frames / freehand aren't votable.

import { useEffect, useRef } from 'react';
import * as Y from 'yjs';
import type { RetroPhase, Shape } from '@frame/shared';
import { castVote, removeVote, votesForShape } from './binding/votesMap';

const VOTEABLE_TYPES: ReadonlySet<Shape['type']> = new Set([
  'sticky',
  'rect',
  'ellipse',
  'text',
]);

interface Args {
  doc: Y.Doc;
  phase: RetroPhase | undefined;
  voteBudget: number;
  userId: string;
  selectedIds: Set<string>;
  setSelectedIds: (ids: Set<string>) => void;
  shapesById: ReadonlyMap<string, Shape>;
}

export function useVoteOnClick({
  doc,
  phase,
  voteBudget,
  userId,
  selectedIds,
  setSelectedIds,
  shapesById,
}: Args) {
  // Don't re-vote on a selection that was already present when the phase
  // turned to vote — only NEW selections trigger voting. Track the last
  // selection we acted on so we don't double-fire across re-renders.
  const lastActedRef = useRef<string | null>(null);

  useEffect(() => {
    if (phase !== 'vote') {
      lastActedRef.current = null;
      return;
    }
    if (selectedIds.size !== 1) {
      lastActedRef.current = null;
      return;
    }
    const onlyId = [...selectedIds][0]!;
    if (lastActedRef.current === onlyId) return;
    const shape = shapesById.get(onlyId);
    if (!shape || !VOTEABLE_TYPES.has(shape.type)) return;
    // Locked shapes are frozen for the retro — the lock badge tells the
    // user this shape can't be modified, and silently casting a vote on
    // it breaks that promise. The selection still clears below, which is
    // intentional: the click registers as "I saw this," it just doesn't
    // mutate the tally.
    if (shape.locked) {
      lastActedRef.current = onlyId;
      setSelectedIds(new Set());
      return;
    }

    const myVotesOnThis = votesForShape(doc, onlyId).get(userId) ?? 0;
    if (myVotesOnThis > 0) {
      // Toggle off: remove one of my votes from this shape.
      removeVote(doc, onlyId, userId);
    } else {
      // Cast a new vote (cap enforces budget).
      castVote(doc, onlyId, userId, voteBudget);
    }

    lastActedRef.current = onlyId;
    // Clear selection so the next click on the same shape registers as a
    // *new* selection event and toggles again.
    setSelectedIds(new Set());
  }, [phase, voteBudget, userId, selectedIds, doc, shapesById, setSelectedIds]);
}
